const shellQuote = (value: string) => `'${value.replaceAll("'", "'\"'\"'")}'`;

export const getByovSetupScript = (
  bucketName: string,
  region: string,
  accountId: string,
  policy: string
) => `#!/usr/bin/env bash
# Enables versioning, expires noncurrent versions after 1 day, removes expired
# delete markers, and adds Eon access.
set -Ee -o pipefail
bucket=${shellQuote(bucketName)}
region=${shellQuote(region)}
owner=${shellQuote(accountId)}
required_policy=${shellQuote(policy)}

# 1. Check local tools before applying changes.
for tool in aws jq diff cmp grep; do
  command -v "$tool" >/dev/null || { echo "Install $tool before running this script." >&2; exit 1; }
done
export AWS_PAGER=''
export AWS_CLI_AUTO_PROMPT=off
umask 077
backup=$(mktemp -d "\${TMPDIR:-/tmp}/eon-byov-backup.XXXXXX")
changed=false
step='Checking AWS sign-in'
fail() {
  trap - ERR
  echo "Error: $1" >&2
  if [ "$changed" = true ]; then
    echo "Some changes may already be applied. Review AWS settings before retrying." >&2
  else
    echo 'No AWS changes were made.' >&2
  fi
  echo "Saved configuration: $backup" >&2
  exit 1
}
trap 'fail "$step failed. Check the error above. For AWS errors, check your sign-in, bucket region, and permissions for the operation shown."' ERR

s3() {
  aws s3api "$@" --bucket "$bucket" --expected-bucket-owner "$owner" --region "$region" --output json
}

read_optional() {
  local operation=$1 missing=$2 output=$3
  if s3 "$operation" > "$output" 2> "$backup/aws-error.txt"; then
    return 0
  fi
  if grep -Fq "($missing)" "$backup/aws-error.txt"; then
    printf 'null\n' > "$output"
  else
    cat "$backup/aws-error.txt" >&2
    return 1
  fi
}

snapshot() {
  s3 get-bucket-versioning > "$backup/versioning-read.json"
  read_optional get-bucket-lifecycle-configuration NoSuchLifecycleConfiguration "$backup/lifecycle-read.json"
  read_optional get-bucket-policy NoSuchBucketPolicy "$backup/policy-read.json"
  jq -S -n --slurpfile versioning "$backup/versioning-read.json" \
    --slurpfile lifecycle "$backup/lifecycle-read.json" --slurpfile policy "$backup/policy-read.json" \
    '{versioning: $versioning[0], lifecycle: $lifecycle[0], policy: ($policy[0] | if . == null then null else .Policy | fromjson end)}'
}

# 2. Confirm the account and save the current bucket settings.
identity=$(aws sts get-caller-identity --region "$region" --output json)
if [ "$(printf '%s' "$identity" | jq -r '.Account')" != "$owner" ]; then
  fail "Your AWS CLI is signed in to a different account. Sign in to $owner and run again."
fi
step='Reading bucket settings'
snapshot > "$backup/before.json"

# 3. Add only missing rules and policy statements; keep existing settings.
step='Preparing changes'
printf '%s\n' "$required_policy" > "$backup/required-policy.json"
jq -S --slurpfile required "$backup/required-policy.json" '
  def as_set: (if type == "array" then . else [.] end) | unique;
  def permission:
    del(.Sid) |
    (.Action, .Resource) |= as_set |
    if (.Principal | type) == "object" then .Principal |= with_entries(.value |= as_set) else . end;
  def covers($rule):
    .Status == "Enabled" and ((.Filter // {Prefix: (.Prefix // "")}) | . == {} or . == {Prefix: ""}) and
    (if $rule.NoncurrentVersionExpiration then .NoncurrentVersionExpiration == $rule.NoncurrentVersionExpiration
     else .Expiration == $rule.Expiration end);
  .versioning.Status = "Enabled" |
  .lifecycle = (.lifecycle // {Rules: []}) |
  {ID: "eon-vault-versioning-cleanup", Status: "Enabled", Filter: {Prefix: ""},
   NoncurrentVersionExpiration: {NoncurrentDays: 1}, Expiration: {ExpiredObjectDeleteMarker: true}} as $rule |
  if any(.lifecycle.Rules[]; covers({NoncurrentVersionExpiration: $rule.NoncurrentVersionExpiration})) and
     any(.lifecycle.Rules[]; covers({Expiration: $rule.Expiration})) then .
  elif any(.lifecycle.Rules[]; .ID == $rule.ID) then
    error("An existing lifecycle rule has different settings: " + $rule.ID + ". Review that rule in AWS before retrying; it has not been overwritten.")
  else .lifecycle.Rules += [$rule] end |
  .policy = (.policy // {Version: "2012-10-17", Statement: []}) |
  .policy.Statement |= (if type == "object" then [.] else . end) |
  reduce $required[0].Statement[] as $statement (.;
    if any(.policy.Statement[]; permission == ($statement | permission)) then .
    elif ($statement.Sid != null and any(.policy.Statement[]; .Sid == $statement.Sid)) then
      error("An existing bucket policy statement has different permissions: " + $statement.Sid + ". Merge the suggested policy using AWS Console setup; it has not been overwritten.")
    else .policy.Statement += [$statement] end
  )
' "$backup/before.json" > "$backup/proposed.json"
# 4. Show changes: minus lines are current values; plus lines are new values.
step='Showing proposed changes'
printf 'Bucket: %s\nRegion: %s\nAccount: %s\nBackup: %s\n' "$bucket" "$region" "$owner" "$backup"
if cmp -s "$backup/before.json" "$backup/proposed.json"; then
  echo 'Required bucket settings are already present. No changes made.'
  exit 0
fi
diff -u "$backup/before.json" "$backup/proposed.json" || { status=$?; [ "$status" -eq 1 ] || exit "$status"; }
# Stop if settings changed while preparing the update.
step='Rechecking bucket settings'
snapshot > "$backup/rechecked.json"
if ! cmp -s "$backup/before.json" "$backup/rechecked.json"; then
  fail 'Bucket settings changed while preparing the update. Run again to use the latest settings.'
fi
# 5. Apply each change and read it back to confirm it succeeded.
for setting in lifecycle versioning policy; do
  if jq -e --arg setting "$setting" --slurpfile before "$backup/before.json" \
    '.[$setting] == $before[0][$setting]' "$backup/proposed.json" >/dev/null; then
    continue
  fi
  step="Updating $setting"
  echo "$step..."
  changed=true
  case "$setting" in
    versioning)
      s3 put-bucket-versioning --versioning-configuration '{"Status":"Enabled"}'
      ;;
    lifecycle)
      jq '.lifecycle | del(.TransitionDefaultMinimumObjectSize)' "$backup/proposed.json" > "$backup/lifecycle.json"
      minimum=$(jq -r '.lifecycle.TransitionDefaultMinimumObjectSize // empty' "$backup/proposed.json")
      extra=()
      if [ -n "$minimum" ]; then extra=(--transition-default-minimum-object-size "$minimum"); fi
      s3 put-bucket-lifecycle-configuration --lifecycle-configuration "file://$backup/lifecycle.json" "\${extra[@]}"
      ;;
    policy)
      jq '.policy' "$backup/proposed.json" > "$backup/policy.json"
      s3 put-bucket-policy --policy "file://$backup/policy.json"
      ;;
  esac
  step="Verifying $setting"
  snapshot > "$backup/verified.json"
  # S3 can add the transition default when creating a lifecycle configuration.
  if ! jq -e --arg setting "$setting" --slurpfile expected "$backup/proposed.json" '
    def as_set: (if type == "array" then . else [.] end) | unique;
    def normalize_policy:
      .Statement |= (as_set | map(
        (.Action, .Resource) |= as_set |
        if (.Principal | type) == "object" then .Principal |= with_entries(.value |= as_set) else . end
      ) | sort);
    .[$setting] as $actual | $expected[0][$setting] as $wanted |
    if $setting == "policy" then ($actual | normalize_policy) == ($wanted | normalize_policy)
    elif $setting == "lifecycle" and ($wanted | has("TransitionDefaultMinimumObjectSize") | not)
    then ($actual | del(.TransitionDefaultMinimumObjectSize)) == $wanted
    else $actual == $wanted end
  ' "$backup/verified.json" >/dev/null; then
    fail "AWS returned unexpected $setting settings. Review this bucket in AWS before retrying."
  fi
  echo "Verified $setting."
done
echo 'Done. Versioning, lifecycle rules, and bucket policy are configured.'
`;
